import { BackendClient } from './services/backend-client';
/**
 * Backend URL öncelik sırası:
 *   1. localStorage override (Ayarlar sayfasından set edilen)
 *   2. Uygulamanın host'undan türet: `app.domain.com` → `api.domain.com`
 *   3. Dev fallback: localhost:5080 (sadece dev-server'da)
 */
function resolveDefaultBackendUrl() {
    const override = localStorage.getItem('photon.backendUrl');
    if (override)
        return override;
    const host = window.location.hostname;
    const protocol = window.location.protocol; // "https:" veya "http:"
    // Vite dev server (localhost:5173): boş string (relative URL) — fetch
    // çağrıları '/auth/login' formunda gider, vite.config.ts proxy ayarı
    // bunları api.photondownload.com'a server-side iletir (CORS bypass).
    if (host === 'localhost' || host === '127.0.0.1' || host.startsWith('192.168.')
        || host.startsWith('10.') || host === '') {
        return '';
    }
    // "app.foo.com" → "api.foo.com"; "foo.com" → "api.foo.com"
    const apiHost = host.startsWith('app.')
        ? 'api.' + host.substring(4)
        : 'api.' + host;
    return `${protocol}//${apiHost}`;
}
/** Signaling URL — aynı logic ama `signal.*` prefix + `wss://` + `/ws`. */
export function resolveDefaultSignalUrl() {
    const override = localStorage.getItem('photon.signalUrl');
    if (override)
        return override;
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
        return 'wss://signal.photondownload.com/ws';
    }
    const sigHost = host.startsWith('app.')
        ? 'signal.' + host.substring(4)
        : 'signal.' + host;
    return `wss://${sigHost}/ws`;
}
/** Relay URL — `relay.*` + WS üzerinden TCP tunnel (:41236). */
export function resolveDefaultRelayUrl() {
    const override = localStorage.getItem('photon.relayUrl');
    if (override)
        return override;
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
        return 'wss://relay.photondownload.com';
    }
    const relayHost = host.startsWith('app.')
        ? 'relay.' + host.substring(4)
        : 'relay.' + host;
    // WsBridge.connect URL'e "/ws?session=...&jwt=..." ekler — burada base URL
    // (path olmadan) dön. Geçmişte "/ws" eklenmesi çift "/ws/ws" oluşturuyordu.
    return `wss://${relayHost}`;
}
/**
 * Kullanıcı önceden lokal dev backend için stale URL override bırakmış
 * olabilir — bu durum UI'da login fail'e yol açar. Lokal'da çalışan dev
 * server için herhangi bir explicit URL override'ı temizle (vite proxy
 * relative URL beklediği için).
 */
(function migrateStaleLocalhostBackend() {
    const isLocalDev = window.location.hostname === 'localhost'
        || window.location.hostname === '127.0.0.1';
    for (const key of ['photon.backendUrl', 'photon.signalUrl', 'photon.relayUrl']) {
        const v = localStorage.getItem(key);
        if (!v)
            continue;
        // localhost dev'de sadece prod URL'leri tut (CORS yok), localhost
        // override'ları sil. Prod'da rahat bırak.
        if (isLocalDev && (v.includes('localhost') || v.includes('127.0.0.1'))) {
            localStorage.removeItem(key);
        }
    }
})();
class AppState {
    listeners = new Set();
    _accessToken = null;
    _userEmail = null;
    _backend = null;
    backendUrl = resolveDefaultBackendUrl();
    // UI redesign Faz 1: shell route. Modern sidebar nav buradan beslenir.
    // Login dışındaki authenticated sayfa ne renderlanacak — değer
    // 'connect' | 'panel' | 'contacts' | 'files' | 'meeting' | 'settings' |
    // 'webauthn' | 'admin' | 'about' | 'session'.
    _currentRoute = 'connect';
    get currentRoute() { return this._currentRoute; }
    navigate(route) {
        if (this._currentRoute === route)
            return;
        this._currentRoute = route;
        this.notify();
    }
    /** Subscriber'ları manuel tetikle — sayfa içi liste yenileme için. */
    refresh() {
        this.notify();
    }
    get isAuthenticated() {
        return this._accessToken !== null;
    }
    get accessToken() {
        return this._accessToken;
    }
    get userEmail() {
        return this._userEmail;
    }
    get backend() {
        if (!this._backend) {
            this._backend = new BackendClient(this.backendUrl);
        }
        return this._backend;
    }
    setBackendUrl(url) {
        this.backendUrl = url;
        localStorage.setItem('photon.backendUrl', url);
        this._backend = new BackendClient(url);
        if (this._accessToken)
            this._backend.setAccessToken(this._accessToken);
        this.notify();
    }
    _isAdmin = false;
    get isAdmin() { return this._isAdmin; }
    async login(email, password) {
        const auth = await this.backend.login(email, password);
        this._accessToken = auth.accessToken;
        this._userEmail = auth.email;
        this._isAdmin = auth.isAdmin === true;
        this.backend.setAccessToken(auth.accessToken);
        this.notify();
    }
    /** Faz 4-21: passkey/WebAuthn ile şifresiz giriş — auth response oturum açar. */
    finishWebAuthnLogin(auth) {
        this._accessToken = auth.accessToken;
        this._userEmail = auth.email;
        this._isAdmin = auth.isAdmin === true;
        this.backend.setAccessToken(auth.accessToken);
        this.notify();
    }
    logout() {
        this._accessToken = null;
        this._userEmail = null;
        this._currentRoute = 'connect';
        this._backend?.setAccessToken(null);
        this.notify();
    }
    subscribe(fn) {
        this.listeners.add(fn);
        return () => this.listeners.delete(fn);
    }
    notify() {
        for (const l of this.listeners)
            l();
    }
}
export const appState = new AppState();
